import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, MessageCircle, Sparkles, Star, Ruler, Zap, Award, ShieldCheck, Truck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { whatsappHref } from "@/lib/settings";

const iconMap = {
  ruler: Ruler, zap: Zap, award: Award, "shield-check": ShieldCheck, truck: Truck, "message-circle": MessageCircle,
};

export const FurnitureTypesSection = ({ types }) => (
  <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20" data-testid="furniture-types-section">
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-600 mb-3">Öne Çıkan Ürünler</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ink-900">Hangi Mobilyayı <span className="gradient-text">Tasarlayalım?</span></h2>
      </div>
      <Link to="/fiyat-hesaplama" className="text-sm font-semibold text-brand-600 hover:text-brand-700 inline-flex items-center gap-1">
        Tümünü Gör <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {types.slice(0, 6).map((t) => (
        <Link
          key={t.id}
          to={`/fiyat-hesaplama?tur=${t.slug}`}
          data-testid={`furniture-type-card-${t.slug}`}
          className="group relative overflow-hidden rounded-3xl bg-white border border-ink-100 hover:brand-shadow hover:-translate-y-1 transition-transform"
        >
          <div className="aspect-[4/3] overflow-hidden">
            <img src={t.image_url} alt={t.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          </div>
          <div className="p-6">
            <h3 className="font-display font-bold text-xl text-ink-900 group-hover:text-brand-600 transition-colors">{t.name}</h3>
            <p className="text-sm text-ink-500 mt-2 line-clamp-2">{t.description}</p>
            <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-600">
              Bu türde tasarım yap <ChevronRight className="h-4 w-4" />
            </div>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

export const WhyUsSection = ({ whyUs }) => (
  <section className="bg-white border-y border-ink-100 py-20" data-testid="why-us-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-600 mb-3">Neden Biz</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ink-900">Güveninizi <span className="gradient-text">Kazanan</span> Detaylar</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {whyUs.map((w, i) => {
          const Icon = iconMap[w.icon] || Sparkles;
          const key = w.title ? `why-${w.title}-${i}` : `why-${i}`;
          return (
            <Card key={key} className="border border-ink-100 rounded-2xl hover:brand-shadow transition-shadow">
              <CardContent className="p-7">
                <div className="h-12 w-12 rounded-xl gradient-primary-soft grid place-items-center text-brand-600 mb-4">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-display font-bold text-lg text-ink-900">{w.title}</h3>
                <p className="text-sm text-ink-500 mt-2 leading-relaxed">{w.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  </section>
);

export const FeaturedWorksSection = ({ featured }) => {
  if (featured.length === 0) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20" data-testid="featured-works-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-600 mb-3">Çalışmalarımız</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ink-900">Son Tamamlanan <span className="gradient-text">Projeler</span></h2>
        </div>
        <Link to="/calismalarimiz" className="text-sm font-semibold text-brand-600 inline-flex items-center gap-1">
          Tüm Çalışmalar <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {featured.map((p) => (
          <div key={p.id} className="group relative aspect-[3/4] overflow-hidden rounded-2xl">
            <img src={p.images?.[0]} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 text-white">
              <div className="text-[10px] font-bold uppercase tracking-widest text-brand-300">{p.furniture_type}</div>
              <div className="font-display text-lg font-bold">{p.title}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export const ProcessSection = ({ steps }) => (
  <section className="bg-ink-900 text-white py-20" data-testid="process-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-300 mb-3">Nasıl Çalışır</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">4 Adımda <span className="gradient-text">Siparişiniz Hazır</span></h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {steps.map((s) => (
          <div key={`step-${s.step}-${s.title}`} className="relative rounded-3xl border border-white/10 bg-white/5 p-7">
            <div className="text-6xl font-display font-black gradient-text opacity-70 leading-none">{String(s.step).padStart(2, "0")}</div>
            <h3 className="mt-4 font-display font-bold text-lg">{s.title}</h3>
            <p className="mt-2 text-sm text-white/70 leading-relaxed">{s.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const TestimonialsSection = ({ testimonials }) => {
  if (testimonials.length === 0) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20" data-testid="testimonials-section">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-600 mb-3">Müşteri Yorumları</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ink-900"><span className="gradient-text">Mutlu</span> Müşteriler</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {testimonials.map((t) => (
          <div key={t.id} className="rounded-3xl bg-white border border-ink-100 p-7 hover:brand-shadow transition-shadow">
            <div className="flex gap-1 text-brand-500 mb-4">
              {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={`star-${t.id}-${i}`} className="h-4 w-4 fill-current" />)}
            </div>
            <p className="text-ink-700 leading-relaxed">"{t.content}"</p>
            <div className="mt-6 pt-5 border-t border-ink-100">
              <div className="font-semibold text-ink-900">{t.name}</div>
              <div className="text-sm text-ink-500">{t.role}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export const LatestBlogSection = ({ posts }) => {
  if (posts.length === 0) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-20" data-testid="latest-blog-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-600 mb-3">Blog</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ink-900">Son <span className="gradient-text">Yazılar</span></h2>
        </div>
        <Link to="/blog" className="text-sm font-semibold text-brand-600 inline-flex items-center gap-1">Tümünü Gör <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {posts.slice(0, 3).map((p) => (
          <Link key={p.id} to={`/blog/${p.slug}`} className="group rounded-3xl overflow-hidden border border-ink-100 bg-white hover:brand-shadow transition-shadow">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
            </div>
            <div className="p-6">
              <div className="text-[10px] font-bold uppercase tracking-widest text-brand-600">{p.category}</div>
              <h3 className="mt-2 font-display font-bold text-lg text-ink-900 group-hover:text-brand-600 transition-colors">{p.title}</h3>
              <p className="mt-2 text-sm text-ink-500 line-clamp-2">{p.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export const BottomCTASection = ({ whatsappNumber }) => (
  <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-20">
    <div className="relative overflow-hidden rounded-[2rem] gradient-primary text-white p-10 sm:p-14 grid md:grid-cols-2 gap-8 items-center brand-shadow-lg">
      <div>
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tight">Projeniz için hemen fiyat alın</h2>
        <p className="mt-4 text-white/90 text-lg">Ölçülerinizi girin, saniyeler içinde detaylı PDF teklifiniz hazır.</p>
      </div>
      <div className="flex flex-wrap gap-3 md:justify-end">
        <Link to="/fiyat-hesaplama">
          <Button size="lg" className="rounded-full bg-white text-brand-700 hover:bg-brand-50 hover:-translate-y-0.5 transition-transform px-7 py-6 font-bold uppercase">
            <Calculator className="h-4 w-4 mr-2" /> Fiyat Hesapla
          </Button>
        </Link>
        <a href={whatsappHref(whatsappNumber)} target="_blank" rel="noreferrer">
          <Button size="lg" variant="outline" className="rounded-full border-2 border-white bg-transparent text-white hover:bg-white hover:text-brand-700 transition-colors px-7 py-6 font-bold uppercase">
            <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
          </Button>
        </a>
      </div>
      <div className="absolute -top-16 -right-10 h-56 w-56 rounded-full bg-white/10" />
      <div className="absolute -bottom-16 -left-10 h-64 w-64 rounded-full bg-black/10" />
    </div>
  </section>
);
